<template>
  <div class="min-h-screen flex flex-col bg-gray-50">
    <div class="flex-1 py-8">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900">Mon Profil</h1>
        <p class="mt-2 text-gray-600">Gérez vos informations personnelles et préférences</p>
      </div>

      <!-- Indicateur de chargement -->
      <div v-if="isLoading" class="flex justify-center items-center py-16">
        <LoadingSpinner size="lg" text="Chargement du profil..." />
      </div>

      <!-- Contenu principal -->
      <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        <!-- Colonne principale - Formulaire -->
        <div class="lg:col-span-2">
          <div class="bg-white shadow-sm rounded-xl border border-gray-200">
            <div class="px-6 py-5 border-b border-gray-200">
              <h2 class="text-lg font-semibold text-gray-900">Informations personnelles</h2>
              <p class="mt-1 text-sm text-gray-500">Mettez à jour vos informations de profil</p>
            </div>
            
            <ProfileForm class="p-6 space-y-6" @submit="saveProfile">
              <!-- Nom -->
              <div>
                <label for="name" class="block text-sm font-medium text-gray-700 mb-2">
                  Nom complet <span class="text-red-500">*</span>
                </label>
                <Field
                  id="name"
                  v-model="user.name"
                  name="name"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Votre nom complet"
                  :rules="validateName"
                />
                <ErrorMessage name="name" class="mt-1 text-sm text-red-600" />
              </div>

              <!-- Email (lecture seule) -->
              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                  Adresse email
                </label>
                <div class="relative">
                  <Field
                    id="email"
                    v-model="user.email"
                    name="email"
                    type="email"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    disabled
                    title="L'email ne peut pas être modifié"
                  />
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <LockIcon class="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                <p class="mt-1 text-sm text-gray-500">
                  L'email ne peut pas être modifié. Contactez un administrateur si nécessaire.
                </p>
              </div>

              <!-- Fonction -->
              <div>
                <label for="fonction" class="block text-sm font-medium text-gray-700 mb-2">
                  Fonction
                </label>
                <Field
                  id="fonction"
                  v-model="user.fonction"
                  name="fonction"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Ex: Développeur, Chercheur, Chef de projet..."
                />
              </div>

              <!-- Affiliation -->
              <div>
                <label for="affiliation" class="block text-sm font-medium text-gray-700 mb-2">
                  Affiliation
                </label>
                <Field
                  id="affiliation"
                  v-model="user.affiliation"
                  name="affiliation"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Ex: Université, Entreprise, Laboratoire..."
                />
              </div>

              <!-- Profil public -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <Field
                      id="is_public"
                      v-model="user.is_public"
                      type="checkbox"
                      name="is_public"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div class="ml-3">
                    <label for="is_public" class="text-sm font-medium text-blue-900">
                      Rendre mon profil public
                    </label>
                    <p class="text-sm text-blue-700 mt-1">
                      Si activé, votre profil sera visible par les autres utilisateurs de la plateforme.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Bouton de sauvegarde -->
              <div class="pt-4">
                <button 
                  type="submit" 
                  class="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="isSaving"
                >
                  <div class="flex items-center justify-center">
                    <LoadingSpinner v-if="isSaving" size="sm" variant="white" :show-text="false" />
                    <SaveIcon v-else class="h-5 w-5 mr-2" />
                    <span>{{ isSaving ? 'Sauvegarde...' : 'Sauvegarder les modifications' }}</span>
                  </div>
                </button>
              </div>
            </ProfileForm>
          </div>
        </div>

        <!-- Colonne latérale - Informations du compte -->
        <div class="lg:col-span-1">
          <div class="bg-white shadow-sm rounded-xl border border-gray-200">
            <div class="px-6 py-5 border-b border-gray-200">
              <h3 class="text-lg font-semibold text-gray-900">Informations du compte</h3>
            </div>
            
            <div class="p-6 space-y-6">
              <!-- Avatar placeholder -->
              <div class="flex justify-center">
                <div class="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span class="text-2xl font-bold text-white">
                    {{ user.name ? user.name.charAt(0).toUpperCase() : 'U' }}
                  </span>
                </div>
              </div>

              <!-- Statistiques du compte -->
              <div class="space-y-4">
                <div class="flex items-center justify-between py-3 border-b border-gray-100">
                  <div class="flex items-center">
                    <ShieldIcon class="h-5 w-5 text-gray-400 mr-3" />
                    <span class="text-sm font-medium text-gray-700">Rôle</span>
                  </div>
                  <span class="text-sm text-gray-900 capitalize bg-gray-100 px-2 py-1 rounded-full">
                    {{ user.role }}
                  </span>
                </div>

                <div class="flex items-center justify-between py-3 border-b border-gray-100">
                  <div class="flex items-center">
                    <CheckCircleIcon v-if="user.email_verified" class="h-5 w-5 text-green-500 mr-3" />
                    <XCircleIcon v-else class="h-5 w-5 text-red-500 mr-3" />
                    <span class="text-sm font-medium text-gray-700">Email vérifié</span>
                  </div>
                  <span 
                    class="text-sm px-2 py-1 rounded-full"
                    :class="user.email_verified ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'"
                  >
                    {{ user.email_verified ? 'Vérifié' : 'Non vérifié' }}
                  </span>
                </div>

                <div class="flex items-center justify-between py-3 border-b border-gray-100">
                  <div class="flex items-center">
                    <CalendarIcon class="h-5 w-5 text-gray-400 mr-3" />
                    <span class="text-sm font-medium text-gray-700">Membre depuis</span>
                  </div>
                  <span class="text-sm text-gray-900">
                    {{ formatDate(user.created_at) }}
                  </span>
                </div>

                <div class="flex items-center justify-between py-3">
                  <div class="flex items-center">
                    <ClockIcon class="h-5 w-5 text-gray-400 mr-3" />
                    <span class="text-sm font-medium text-gray-700">Dernière modification</span>
                  </div>
                  <span class="text-sm text-gray-900">
                    {{ formatDate(user.updated_at) }}
                  </span>
                </div>
              </div>

              <!-- Actions rapides -->
              <div class="pt-4 border-t border-gray-200">
                <h4 class="text-sm font-medium text-gray-700 mb-3">Actions rapides</h4>
                <div class="space-y-2">
                  <button 
                    type="button"
                    class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    @click="showChangePasswordModal = true"
                  >
                    <KeyIcon class="h-4 w-4 inline mr-2" />
                    Changer le mot de passe
                  </button>
                  <button 
                    type="button"
                    class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    @click="downloadData"
                  >
                    <DownloadIcon class="h-4 w-4 inline mr-2" />
                    Télécharger mes données
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Notifications -->
    <Notification ref="notification" />
  </div>

  <!-- Footer collé en bas avec pleine largeur -->
  <AppFooter />

  <!-- Modal de changement de mot de passe -->
  <ChangePasswordModal 
    v-model="showChangePasswordModal"
    @success="handlePasswordChangeSuccess"
    @error="handlePasswordChangeError"
  />
</div>
</template>

<script setup lang="ts">
import type { UserUpdate } from '@/api/types/core/users';
import { users } from '@/api/users';
import ChangePasswordModal from '@/components/auth/ChangePasswordModal.vue';
import Notification from '@/components/Notification.vue';
import AppFooter from '@/components/UI/AppFooter.vue';
import LoadingSpinner from '@/components/UI/LoadingSpinner.vue';
import { useAuthStore } from '@/stores/auth';
import type { User } from '@/types/auth';
import {
    CalendarIcon,
    CheckCircleIcon,
    ClockIcon,
    DownloadIcon,
    KeyIcon,
    LockIcon,
    SaveIcon,
    ShieldIcon,
    XCircleIcon
} from 'lucide-vue-next';
import { ErrorMessage, Field, Form as ProfileForm } from 'vee-validate';
import { onMounted, ref } from 'vue';

// Composables
const authStore = useAuthStore();

// Références
const notification = ref<InstanceType<typeof Notification> | null>(null);

// État local
const user = ref<User>({
  uuid: '',
  email: '',
  name: '',
  role: 'user',
  fonction: null,
  affiliation: null,
  email_verified: false,
  is_public: true,
  created_at: '',
  updated_at: '',
});

const isLoading = ref(false);
const isSaving = ref(false);
const showChangePasswordModal = ref(false);

// Validation
const validateName = (value: unknown): string | true => {
  const stringValue = String(value || '');
  if (!stringValue || stringValue.trim().length === 0) {
    return 'Le nom est requis';
  }
  if (stringValue.trim().length < 2) {
    return 'Le nom doit contenir au moins 2 caractères';
  }
  if (stringValue.trim().length > 100) {
    return 'Le nom ne peut pas dépasser 100 caractères';
  }
  return true;
};

// Utilitaires
const formatDate = (dateString: string): string => {
  if (!dateString) return 'Non disponible';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'Date invalide';
  }
};

// Actions
const fetchUserData = async (): Promise<void> => {
  isLoading.value = true;
  
  try {
    // Utiliser les données du store d'authentification
    if (authStore.user) {
      user.value = { ...authStore.user };
    } else {
      // Fallback: récupérer depuis l'API
      const userData = await users.getCurrentUser();
      user.value = userData;
    }
  } catch (error) {
    console.error('Erreur lors de la récupération du profil:', error);
    notification.value?.showNotification(
      'Erreur lors de la récupération des informations du profil.',
      'error'
    );
  } finally {
    isLoading.value = false;
  }
};

const saveProfile = async (): Promise<void> => {
  isSaving.value = true;
  
  try {
    // Préparer les données à envoyer
    const updateData: UserUpdate = {
      name: user.value.name.trim(),
      fonction: user.value.fonction?.trim() || undefined,
      affiliation: user.value.affiliation?.trim() || undefined,
      is_public: user.value.is_public,
    };
    
    // Mettre à jour via l'API
    const updatedUser = await users.updateCurrentUser(updateData);
    
    // Mettre à jour les données locales
    user.value = updatedUser;
    
    // Mettre à jour le store d'authentification
    await authStore.refreshUserData();
    
    notification.value?.showNotification(
      'Profil sauvegardé avec succès !',
      'success'
    );
    
  } catch (error) {
    console.error('Erreur lors de la sauvegarde du profil:', error);
    notification.value?.showNotification(
      'Erreur lors de la sauvegarde du profil. Veuillez réessayer.',
      'error'
    );
  } finally {
    isSaving.value = false;
  }
};

const downloadData = (): void => {
  notification.value?.showNotification(
    'Fonctionnalité de téléchargement des données en cours de développement.',
    'info'
  );
};

const handlePasswordChangeSuccess = (): void => {
  notification.value?.showNotification(
    'Mot de passe modifié avec succès !',
    'success'
  );
};

const handlePasswordChangeError = (error: string): void => {
  notification.value?.showNotification(
    `Erreur lors du changement de mot de passe : ${error}`,
    'error'
  );
};

// Lifecycle
onMounted(() => {
  fetchUserData();
});
</script>
